import { ButtonColor } from "@/common/enum/button-color";
import { LoginPath } from "@/common/enum/path";
import { TextInputColor } from "@/common/enum/text-input-color";
import { formatBusinessNumber } from "@/common/util/format-business-number";
import { formatPhoneNumber } from "@/common/util/format-phone-number";
import { validatePhoneNumber } from "@/common/validator/validate-phone-number";
import { userService } from "@/services/user-service";
import { Button, TextInput } from "flowbite-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CustomHelperText } from "../CustomHelperText";
import { TextInputLabel } from "../label/TextInputLabel";
import { Body14Regular } from "../typography/Body14Regular";
import { Body14Underline } from "../typography/Body14Underline";

const initFindPartnerPasswordPayload = {
  businessNumber: "",
  email: "",
  phone: "",
};

export const FindPasswordPanel = () => {
  const navigate = useNavigate();
  const [findPartnerPasswordPayload, setFindPartnerPasswordPayload] = useState(
    initFindPartnerPasswordPayload
  );
  const [isLinkSent, setIsLinkSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [aggregateErrors, setAggregateErrors] = useState({
    businessNumber: "",
    email: "",
    phone: "",
  });

  const isFindPartnerPasswordButtonActive =
    findPartnerPasswordPayload.businessNumber &&
    findPartnerPasswordPayload.email &&
    findPartnerPasswordPayload.phone;

  const handleFindPartnerPasswordPayloadChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    setFindPartnerPasswordPayload({
      ...findPartnerPasswordPayload,
      [name]: value,
    });
  };

  const handleBusinessNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formattedValue = formatBusinessNumber(event.target.value);

    setFindPartnerPasswordPayload({
      ...findPartnerPasswordPayload,
      businessNumber: formattedValue,
    });
  };

  const handlePhoneNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formattedValue = formatPhoneNumber(event.target.value);

    setFindPartnerPasswordPayload({
      ...findPartnerPasswordPayload,
      phone: formattedValue,
    });
  };

  const handleFindPartnerPasswordButtonClick = async () => {
    try {
      if (!validatePhoneNumber(findPartnerPasswordPayload.phone)) {
        setAggregateErrors({
          ...aggregateErrors,
          phone: "올바른 휴대폰 번호를 입력해주세요",
        });
        return;
      }

      const response = await userService.findPartnerPassword(
        findPartnerPasswordPayload
      );
      setAggregateErrors({
        businessNumber: response.data.aggregateErrors?.businessNumber || "",
        email: response.data.aggregateErrors?.email || "",
        phone: response.data.aggregateErrors?.phone || "",
      });
      if (response.ok) {
        setIsLinkSent(true);
        setCountdown(60);
      } else {
        setIsLinkSent(false);
      }
    } catch (error) {
      console.log(error);
      setIsLinkSent(false);
    }
  };

  const handleLoginClick = async () => {
    navigate(LoginPath.LOGIN);
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  return (
    <div className="flex flex-col w-full">
      <TextInputLabel required>사업자 번호</TextInputLabel>
      <TextInput
        required
        placeholder="000-00-00000"
        name="businessNumber"
        value={findPartnerPasswordPayload.businessNumber}
        onChange={handleBusinessNumberChange}
        color={
          !!aggregateErrors.businessNumber
            ? TextInputColor.FAILURE
            : TextInputColor.GRAY
        }
      />
      {!!aggregateErrors.businessNumber && (
        <CustomHelperText
          isError={!!aggregateErrors.businessNumber}
          text={aggregateErrors.businessNumber}
        />
      )}
      <div className="mt-[16px]">
        <TextInputLabel required>아이디</TextInputLabel>
      </div>
      <TextInput
        required
        placeholder="aqara@gmail.com"
        name="email"
        value={findPartnerPasswordPayload.email}
        onChange={handleFindPartnerPasswordPayloadChange}
        color={
          !!aggregateErrors.email ? TextInputColor.FAILURE : TextInputColor.GRAY
        }
      />
      {!!aggregateErrors.email && (
        <CustomHelperText
          isError={!!aggregateErrors.email}
          text={aggregateErrors.email}
        />
      )}
      <div className="mt-[16px]">
        <TextInputLabel required>연락처</TextInputLabel>
      </div>
      <TextInput
        required
        placeholder="010-1234-1234"
        name="phone"
        value={findPartnerPasswordPayload.phone}
        onChange={handlePhoneNumberChange}
        color={
          !!aggregateErrors.phone ? TextInputColor.FAILURE : TextInputColor.GRAY
        }
      />
      <CustomHelperText
        isError={!!aggregateErrors.phone}
        text={
          aggregateErrors.phone || "가입 시 입력한 휴대폰 번호를 입력해주세요"
        }
      />
      {isLinkSent && (
        <Body14Regular className="text-center mt-[32px] text-[#001AFF]">
          입력한 이메일로 링크가 전송되었습니다.
        </Body14Regular>
      )}
      <Button
        className={isLinkSent ? "mt-[24px]" : "mt-[32px]"}
        color={
          isFindPartnerPasswordButtonActive && countdown === 0
            ? ButtonColor.PRIMARY
            : ButtonColor.DISABLED
        }
        disabled={!isFindPartnerPasswordButtonActive || countdown > 0}
        onClick={handleFindPartnerPasswordButtonClick}
      >
        {countdown > 0
          ? `${countdown}s 한 번 더 링크 전송하기`
          : "이메일로 비밀번호 변경 링크 전송"}
      </Button>
      <div className="flex justify-center mt-[24px] text-semantic-text-body">
        <div onClick={handleLoginClick}>
          <Body14Underline className="text-center">로그인 하기</Body14Underline>
        </div>
      </div>
    </div>
  );
};
